// ChartsView.jsx
// Reusable chart layout component — single or grid
// Used by Dashboard.jsx (main charts view) and TradesView.jsx (trade review)
// No chart logic here — just layout + WatchlistPanel + Chart instances

import React, { useEffect } from 'react';
import useStore from '../store';

import ChartNew from './ChartNew';
import WatchlistPanel from './WatchlistPanel';

// ============================================================
// TRADES SYMBOL STRIP — replaces WatchlistPanel in trades mode.
// Lists the loaded-selection symbols; clicking one drives the chart.
// ============================================================
function TradesSymbolStrip({ symbols = [], activeSymbol, onPick }) {
  const [collapsed, setCollapsed] = React.useState(false);
  return (
    <div style={stripStyles.wrapper(collapsed)}>
      <button
        style={stripStyles.toggleBtn}
        onClick={() => setCollapsed(!collapsed)}
        title={collapsed ? 'Expand symbols' : 'Collapse symbols'}
      >
        {collapsed ? '›' : '‹'}
      </button>
      {!collapsed && (
        <div style={stripStyles.list}>
          <div style={stripStyles.header}>TRADES</div>
          {symbols.map((sym) => (
            <div
              key={sym}
              style={{
                ...stripStyles.item,
                ...(sym === activeSymbol ? stripStyles.itemActive : {}),
              }}
              onClick={() => onPick && onPick(sym)}
              title={sym}
            >
              {sym}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const stripStyles = {
  wrapper: (collapsed) => ({
    position:        'relative',
    width:           collapsed ? '18px' : '110px',
    minWidth:        collapsed ? '18px' : '110px',
    height:          '100%',
    backgroundColor: '#fafafa',
    borderRight:     '1px solid #e0e0e0',
    display:         'flex',
    flexDirection:   'column',
    transition:      'width 0.2s ease, min-width 0.2s ease',
    flexShrink:      0,
    overflow:        'hidden',
    zIndex:          10,
  }),
  toggleBtn: {
    position: 'absolute', right: '-1px', top: '50%',
    transform: 'translateY(-50%)', width: '16px', height: '40px',
    backgroundColor: '#e8f0fe', border: '1px solid #c5d0e8',
    borderLeft: 'none', borderRadius: '0 4px 4px 0', cursor: 'pointer',
    fontSize: '12px', color: '#2962ff', display: 'flex',
    alignItems: 'center', justifyContent: 'center', zIndex: 11, padding: 0,
  },
  list: { flex: 1, overflowY: 'auto', overflowX: 'hidden' },
  header: {
    padding: '6px 8px', fontSize: '9px', color: '#999', fontWeight: 'bold',
    letterSpacing: '0.5px', borderBottom: '1px solid #eee',
  },
  item: {
    padding: '6px 8px', fontSize: '12px', color: '#333', cursor: 'pointer',
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
    userSelect: 'none',
  },
  itemActive: { backgroundColor: '#e8f0fe', color: '#2962ff', fontWeight: 'bold' },
};


function ChartsView({ tradesMode = false, tradesSymbols = [], activeSymbol = null, onSymbolPick = null }) {
  const { layout, setLayout, gridCount, setGridCount, watchlist, showMarkers, setShowMarkers } = useStore();

  // In trades mode the symbol list comes from the loaded selection,
  // NOT the global watchlist — this keeps Trades scoped and stops the
  // temp symbols leaking into the Charts menu.
  const symbolList = tradesMode ? tradesSymbols : watchlist;

  // Empty-state gate — applies to whichever list drives this instance.
  if (symbolList.length === 0) {
    return (
      <div style={styles.noDefault}>
        <div style={styles.noDefaultIcon}>📋</div>
        <div style={styles.noDefaultTitle}>
          {tradesMode ? 'No trades loaded' : 'No default watchlist set'}
        </div>
        <div style={styles.noDefaultText}>
          {tradesMode
            ? 'Set filters and press "Load on Chart" to review trades.'
            : 'Go to Watchlists, create a watchlist, add stocks, then click "Set Default".'}
        </div>
      </div>
    );
  }

  const chartSlots = Array.from({ length: gridCount }, (_, i) => i);
  const gridStyle  = { 2: styles.grid2, 3: styles.grid3, 4: styles.grid4 }[gridCount] || styles.grid4;

  return (
    <div style={styles.viewContainer}>

      {/* Layout + controls bar */}
      <div style={styles.layoutBar}>
        <span style={styles.label}>Layout:</span>
        <button
          onClick={() => setLayout('single')}
          style={{ ...styles.btn, ...(layout === 'single' ? styles.btnActive : {}) }}
        >Single</button>

        {[2, 3, 4].map((n) => (
          <button
            key={n}
            onClick={() => { setLayout('grid'); setGridCount(n); }}
            style={{
              ...styles.btn,
              ...(layout === 'grid' && gridCount === n ? styles.btnActive : {}),
            }}
          >
            {n === 2 ? '1×2' : n === 3 ? '1+2' : '2×2'}
          </button>
        ))}

        <span style={styles.divider} />

        {/* Markers toggle */}
        <label style={styles.checkLabel}>
          <input
            type="checkbox"
            checked={showMarkers}
            onChange={(e) => setShowMarkers(e.target.checked)}
            style={{ marginRight: 4 }}
          />
          Trade Markers
        </label>
      </div>

      {/* Charts + Watchlist Panel */}
      <div style={styles.chartArea}>
        {tradesMode ? (
          <TradesSymbolStrip
            symbols={tradesSymbols}
            activeSymbol={activeSymbol}
            onPick={onSymbolPick}
          />
        ) : (
          <WatchlistPanel />
        )}
        {layout === 'single' ? (
          <div style={styles.single}>
            <ChartNew gridIndex={0} isGrid={false} />
          </div>
        ) : (
          <div style={gridStyle}>
            {chartSlots.map((i) => (
              <div
                key={i}
                style={
                  gridCount === 3 && i === 2
                    ? styles.spanRight
                    : styles.chartSlot
                }
              >
                <ChartNew gridIndex={i} isGrid={true} />
              </div>
            ))}
          </div>
        )}
      </div>

    </div>
  );
}

// ============================================================
// STYLES
// ============================================================
const styles = {
  viewContainer: {
    display:       'flex',
    flexDirection: 'column',
    flex:          1,
    overflow:      'hidden',
  },
  layoutBar: {
    display:         'flex',
    alignItems:      'center',
    gap:             '6px',
    padding:         '4px 16px',
    backgroundColor: '#ffffff',
    borderBottom:    '1px solid #e0e0e0',
    flexShrink:      0,
  },
  label: {
    color:    '#666',
    fontSize: '12px',
  },
  btn: {
    padding:         '3px 10px',
    backgroundColor: '#f0f0f0',
    color:           '#666',
    border:          '1px solid #ddd',
    borderRadius:    '4px',
    cursor:          'pointer',
    fontSize:        '12px',
  },
  btnActive: {
    backgroundColor: '#2962ff',
    color:           '#ffffff',
    borderColor:     '#2962ff',
  },
  divider: {
    display:         'inline-block',
    width:           '1px',
    height:          '16px',
    backgroundColor: '#e0e0e0',
    margin:          '0 4px',
  },
  checkLabel: {
    display:    'flex',
    alignItems: 'center',
    fontSize:   '12px',
    color:      '#444',
    cursor:     'pointer',
  },
  chartArea: {
    display:  'flex',
    flex:     1,
    overflow: 'hidden',
    minWidth: 0,
  },
  single: {
    flex:     1,
    padding:  '4px',
    overflow: 'hidden',
    display:  'flex',
    minWidth: 0,
  },
  grid2: {
    display:             'grid',
    gridTemplateColumns: '1fr 1fr',
    gridTemplateRows:    '1fr',
    flex:                1,
    gap:                 '4px',
    padding:             '4px',
    backgroundColor:     '#f5f5f5',
    overflow:            'hidden',
  },
  grid3: {
    display:             'grid',
    gridTemplateColumns: '1fr 1fr',
    gridTemplateRows:    '1fr 1fr',
    flex:                1,
    gap:                 '4px',
    padding:             '4px',
    backgroundColor:     '#f5f5f5',
    overflow:            'hidden',
  },
  grid4: {
    display:             'grid',
    gridTemplateColumns: '1fr 1fr',
    gridTemplateRows:    '1fr 1fr',
    flex:                1,
    gap:                 '4px',
    padding:             '4px',
    backgroundColor:     '#f5f5f5',
    overflow:            'hidden',
  },
  chartSlot: {
    overflow:  'hidden',
    minWidth:  0,
    minHeight: 0,
    display:   'flex',
  },
  spanRight: {
    gridColumn: 2,
    gridRow:    '1 / 3',
    overflow:   'hidden',
    minWidth:   0,
    minHeight:  0,
    display:    'flex',
  },
  noDefault: {
    flex:           1,
    display:        'flex',
    flexDirection:  'column',
    alignItems:     'center',
    justifyContent: 'center',
    gap:            '12px',
    color:          '#666',
  },
  noDefaultIcon:  { fontSize: '48px' },
  noDefaultTitle: { fontSize: '18px', fontWeight: 'bold', color: '#444' },
  noDefaultText:  { fontSize: '14px', color: '#888', textAlign: 'center', maxWidth: '320px', lineHeight: '1.6' },
};

export default ChartsView;